function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useEffect, useMemo, useState } from 'react';
import { orderBy, sortBy } from 'lodash-es';
import { CustomModal, Button, TableList, Page, Spinner, Typography, PageEmptyState, PageErrorState, PageContentHeader, useStateInRef, FilterPickerGroup, Searchbox, MultiSelectFilterPicker } from 'seagull';
import { useAppConfig, useStore, useStoreActions } from "../../../store/store-context";
import { CreateProposalButton } from "../../../components/CreateProposalButton/CreateProposalButton";
import { handlePromise } from "../../../infra/utils";
import { useServiceTemplateApi } from "../../../hooks/useServiceTemplateApi";
import { AccountingStatus } from "../../../types/api/accounting-integration";
import { selectors } from "../../../store/state-selectors";
import { useOnMount } from "../../../hooks/useOnMount";
import NoDataIcon from "../../assets/NoDataIcon";
import styles from "./MapServicesModal.module.scss";
import { columnDefs, getSearchedServicesAndFilteredOptions, getHeaderItems } from "./MapServicesModal.utils";
import MapServicesRow from "./MapServicesRow";
import { serviceTemplateModel } from "../../../models";
import { getAccountingDetails } from "../accounting.utils";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { filterServiceOnlyTemplates } from "../../../utils/filterServiceOnlyTemplates";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export function MapServicesModal(_ref) {
  let {
    dismissModal,
    onChange,
    serviceTemplateIds,
    accounting
  } = _ref;
  const {
    data: accountingItems,
    loading: loadingAccountingItems,
    error: errorAccountingItems
  } = useStore(selectors.integration.accounting(accounting).items);
  const {
    fetchAccountingItems
  } = useStoreActions();
  const {
    SERVICE_TEMPLATE_BUNDLE
  } = useFeatureFlags();
  const businessId = useStore(selectors.user.businessId);
  const {
    api,
    logBI
  } = useStoreActions();
  const {
    inputDebounceTime
  } = useAppConfig();
  const [modalState, setModalState] = useState('loading');
  const [serviceTemplates, setServiceTemplates] = useState();
  const [loadingServiceTemplates, setLoadingServiceTemplates] = useState(true);
  const [errorServiceTemplates, setErrorServiceTemplates] = useState(false);
  const [search, setSearch] = useState('');
  const [serviceMappingStatusFilter, setServiceMappingStatusFilter] = useState([]);
  const serviceTemplateApi = useServiceTemplateApi();
  const isLoading = !accountingItems && loadingAccountingItems || !serviceTemplates && loadingServiceTemplates;
  const vendorId = useStore(selectors.user.businessId);
  const userId = useStore(selectors.user.id);
  const {
    testIdPrefix,
    biCodes,
    fullName
  } = getAccountingDetails(accounting);
  const getStateInRef = useStateInRef({
    serviceTemplates,
    errorAccountingItems,
    errorServiceTemplates,
    accountingItems,
    userId,
    vendorId
  });
  const {
    filteredServices,
    serviceFilterOptions
  } = useMemo(() => getSearchedServicesAndFilteredOptions({
    services: serviceTemplates || [],
    search,
    statusFilters: serviceMappingStatusFilter,
    accounting
  }), [serviceTemplates, search, serviceMappingStatusFilter, accounting]);
  useOnMount(() => {
    refreshData();
  });
  useEffect(() => {
    const {
      serviceTemplates,
      errorAccountingItems,
      errorServiceTemplates,
      accountingItems,
      userId,
      vendorId
    } = getStateInRef();
    const hasError = errorAccountingItems || (accountingItems === null || accountingItems === void 0 ? void 0 : accountingItems.status) === AccountingStatus.RequestTimeout || errorServiceTemplates;
    const noAnchorServiceTemplates = serviceTemplates && serviceTemplates.length === 0;
    const hasData = serviceTemplates && accountingItems;
    if (isLoading) setModalState('loading');else if (hasError) setModalState('error');else if (noAnchorServiceTemplates) setModalState('empty');else if (hasData) setModalState('valid');
    if (hasError) {
      logBI(biCodes.linkService, {
        vendorId,
        userId
      });
    }
  }, [biCodes.linkService, isLoading]);
  async function fetchServiceTemplates() {
    setLoadingServiceTemplates(true);
    try {
      const services = await serviceTemplateApi.getForMappingToAccountingIntegration();
      const filteredServices = serviceTemplateIds !== null && serviceTemplateIds !== void 0 && serviceTemplateIds.length ? services.filter(s => serviceTemplateIds.includes(s.id)) : services;
      const orderedByName = orderBy(filteredServices, ['name'], ['asc']);
      const sortedByIntegrationStatus = sortBy(orderedByName, serviceTemplate => !!serviceTemplateModel.integratedId({
        serviceTemplate,
        accounting
      }));
      const sortedByDeletedStatus = sortBy(sortedByIntegrationStatus, serviceTemplateModel.isDeleted);
      setServiceTemplates(sortedByDeletedStatus);
    } catch (err) {
      setErrorServiceTemplates(true);
    } finally {
      setLoadingServiceTemplates(false);
    }
  }
  const numOfMappedServices = useMemo(() => {
    const mappedServices = serviceTemplates === null || serviceTemplates === void 0 ? void 0 : serviceTemplates.filter(serviceTemplate => !!serviceTemplateModel.integratedId({
      serviceTemplate,
      accounting
    }));
    return (mappedServices === null || mappedServices === void 0 ? void 0 : mappedServices.length) || 0;
  }, [accounting, serviceTemplates]);
  const accountingItemsDictionary = useMemo(() => (accountingItems === null || accountingItems === void 0 ? void 0 : accountingItems.items.reduce((acc, item) => _objectSpread(_objectSpread({}, acc), {}, {
    [item.id]: item
  }), {})) || {}, [accountingItems]);
  const serviceOnlyItems = useMemo(() => filterServiceOnlyTemplates((accountingItems === null || accountingItems === void 0 ? void 0 : accountingItems.items) || []), [accountingItems]);
  async function refreshData() {
    fetchServiceTemplates();
    fetchAccountingItems(accounting, !!SERVICE_TEMPLATE_BUNDLE);
  }
  async function onChangeMap(serviceTemplate, accountingItem) {
    const linkResponse = await handlePromise(api.accounting.linkServiceTemplate(_objectSpread({
      accounting,
      serviceTemplateId: serviceTemplate.id
    }, accountingItem ? {
      integratedId: accountingItem.id,
      type: accountingItem.type
    } : {})));
    if (linkResponse.success) {
      onChange && onChange({
        serviceTemplateId: serviceTemplate.id,
        accountingItemId: accountingItem === null || accountingItem === void 0 ? void 0 : accountingItem.id
      });
      const updatedServiceTemplate = await handlePromise(api.serviceTemplate.getById(businessId, serviceTemplate.id));
      if (updatedServiceTemplate.success) {
        setServiceTemplates(prev => {
          const newAnchorServiceTemplates = prev === null || prev === void 0 ? void 0 : prev.map(s => {
            const isDesiredServiceTemplate = s.id == serviceTemplate.id;
            return isDesiredServiceTemplate ? updatedServiceTemplate.data : s;
          });
          return newAnchorServiceTemplates;
        });
      } else {
        throw updatedServiceTemplate.error;
      }
    } else {
      throw linkResponse.error;
    }
  }
  return /*#__PURE__*/_jsx(CustomModal, {
    "data-testid": "".concat(testIdPrefix, "-map-services-modal"),
    footer: modalState === 'valid' && /*#__PURE__*/_jsxs("div", {
      className: styles.footer,
      children: [serviceTemplates && /*#__PURE__*/_jsx("div", {
        "data-testid": "total-map-status",
        children: /*#__PURE__*/_jsx(Typography, {
          children: "".concat(numOfMappedServices, " of ").concat(serviceTemplates.length, " services mapped")
        })
      }), /*#__PURE__*/_jsx(Button, {
        onClick: dismissModal,
        "data-testid": "".concat(testIdPrefix, "-map-services-close"),
        children: "Close"
      })]
    }),
    height: "720px",
    minWidth: "1280px",
    onClose: dismissModal,
    children: /*#__PURE__*/_jsx(Page, {
      height: "100%",
      header: "Map Services to ".concat(fullName),
      subheader: /*#__PURE__*/_jsxs(_Fragment, {
        children: ["Map Anchor services to ", fullName, " items."]
      }),
      skin: "secondary",
      "data-testid": "".concat(testIdPrefix, "-map-services"),
      children: /*#__PURE__*/_jsxs(TableList.Provider, {
        columnDefs: columnDefs(!SERVICE_TEMPLATE_BUNDLE),
        children: [modalState === 'loading' && /*#__PURE__*/_jsx(LoadingState, {}), modalState === 'empty' && /*#__PURE__*/_jsx(EmptyState, {
          accounting: accounting
        }), modalState === 'error' && /*#__PURE__*/_jsx(ErrorState, {
          onRefresh: refreshData
        }), modalState === 'valid' && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsxs(PageContentHeader, {
            children: [/*#__PURE__*/_jsx("div", {
              className: styles.filterContainer,
              children: /*#__PURE__*/_jsxs(FilterPickerGroup, {
                flexWrap: true,
                "data-testid": "map-services-modal-filters",
                children: [/*#__PURE__*/_jsx(Searchbox, {
                  value: search,
                  "data-testid": "map-services-modal-search",
                  debounceTime: inputDebounceTime !== null && inputDebounceTime !== void 0 ? inputDebounceTime : 500,
                  onChange: event => setSearch(event.target.value),
                  onClear: () => setSearch('')
                }), /*#__PURE__*/_jsx(MultiSelectFilterPicker, {
                  "data-testid": "".concat(testIdPrefix, "-mapping-status-filter-picker"),
                  value: serviceMappingStatusFilter,
                  options: serviceFilterOptions,
                  label: "Is mapped",
                  onChange: setServiceMappingStatusFilter
                })]
              })
            }), /*#__PURE__*/_jsx(TableList.Header, {
              items: getHeaderItems(accounting, !SERVICE_TEMPLATE_BUNDLE)
            })]
          }), filteredServices === null || filteredServices === void 0 ? void 0 : filteredServices.map((serviceTemplate, index) => {
            const integratedId = serviceTemplateModel.integratedId({
              serviceTemplate,
              accounting
            });
            const integratedItem = integratedId ? accountingItemsDictionary[integratedId] : undefined;
            const items = serviceTemplateModel.isBundle(serviceTemplate) && SERVICE_TEMPLATE_BUNDLE ? (accountingItems === null || accountingItems === void 0 ? void 0 : accountingItems.items) || [] : serviceOnlyItems;
            return /*#__PURE__*/_jsx(MapServicesRow, {
              serviceTemplate: serviceTemplate,
              accountingItems: items,
              integratedItem: integratedItem,
              onChangeMap: onChangeMap,
              testid: "map-services-row-".concat(index),
              accounting: accounting
            }, serviceTemplate.id);
          })]
        })]
      })
    })
  });
}
const LoadingState = () => /*#__PURE__*/_jsx("div", {
  className: styles.loading,
  children: /*#__PURE__*/_jsx(Spinner, {
    size: 40,
    "data-testid": "map-services-loading"
  })
});
const EmptyState = _ref2 => {
  let {
    accounting
  } = _ref2;
  return /*#__PURE__*/_jsx(PageEmptyState, {
    "data-testid": "map-services-empty-state",
    illustration: /*#__PURE__*/_jsx(NoDataIcon, {}),
    message: "In order to map with your ".concat(getAccountingDetails(accounting).fullName, " account, start by creating an agreement"),
    actions: /*#__PURE__*/_jsx(CreateProposalButton, {
      text: "Create a new agreement",
      hidePrefix: true,
      "data-testid": "map-service-new-btn"
    })
  });
};
const ErrorState = _ref3 => {
  let {
    onRefresh
  } = _ref3;
  return /*#__PURE__*/_jsx(PageErrorState, {
    "data-testid": "map-services-error-state",
    message: "Failed to retrieve the relevant data please try again later",
    onRefresh: onRefresh,
    skin: "secondary"
  });
};